/**
 * Cart Redux slice
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CartItem, SavedItem, AddToCartPayload, UpdateQuantityPayload } from '@/types';
import type { AppliedCoupon } from '@/types';
import { computeCouponDiscount } from '@/types/coupon.types';
import type { RootState } from '@/store';
import {
  FREE_SHIPPING_THRESHOLD,
  DEFAULT_SHIPPING_COST,
  GST_RATE,
  MAX_CART_ITEMS,
} from '@/config/constants';

/**
 * Builds a composite identifier for a cart item.
 * Regular items: "productId", sized items: "productId::selectedSize"
 */
export const getCartItemId = (item: CartItem): string =>
  item.selectedSize ? `${item.product.id}::${item.selectedSize}` : item.product.id;

/**
 * Composite identifier for a saved-for-later item (same scheme as cart items)
 * so size variants stay distinct: "productId" or "productId::selectedSize".
 */
export const getSavedItemId = (item: SavedItem): string =>
  item.selectedSize ? `${item.product.id}::${item.selectedSize}` : item.product.id;

/**
 * Splits a composite identifier back into its productId + optional selectedSize.
 */
export const parseCartItemId = (
  id: string
): { productId: string; selectedSize?: string } => {
  const separatorIndex = id.indexOf('::');
  if (separatorIndex === -1) return { productId: id };
  return {
    productId: id.slice(0, separatorIndex),
    selectedSize: id.slice(separatorIndex + 2),
  };
};

const matchesId = (item: CartItem, identifier: string): boolean =>
  getCartItemId(item) === identifier;

const matchesSavedId = (item: SavedItem, identifier: string): boolean =>
  getSavedItemId(item) === identifier;

interface CartState {
  items: CartItem[];
  savedForLater: SavedItem[];
  isDrawerOpen: boolean;
  savedForLaterLoading: boolean;
  appliedCoupon: AppliedCoupon | null;
}

const initialState: CartState = {
  items: [],
  savedForLater: [],
  isDrawerOpen: false,
  savedForLaterLoading: false,
  appliedCoupon: null,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    /**
     * Add item to cart (matches on product.id + selectedSize)
     */
    addToCart: (state, action: PayloadAction<AddToCartPayload>) => {
      const { product, quantity, selectedSize } = action.payload;
      const existingItem = state.items.find(
        (item) => item.product.id === product.id && item.selectedSize === selectedSize
      );

      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        if (state.items.length >= MAX_CART_ITEMS) return;
        state.items.push({
          product,
          quantity,
          selectedSize,
          addedAt: new Date().toISOString(),
        });
      }
    },

    /**
     * Remove item from cart by composite identifier
     */
    removeFromCart: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(
        (item) => !matchesId(item, action.payload)
      );
    },

    /**
     * Update item quantity (uses composite identifier in productId field)
     */
    updateQuantity: (state, action: PayloadAction<UpdateQuantityPayload>) => {
      const { productId, quantity } = action.payload;
      const item = state.items.find((i) => matchesId(i, productId));

      if (item) {
        if (quantity <= 0) {
          state.items = state.items.filter((i) => !matchesId(i, productId));
        } else {
          item.quantity = quantity;
        }
      }
    },

    /**
     * Increment item quantity by composite identifier
     */
    incrementQuantity: (state, action: PayloadAction<string>) => {
      const item = state.items.find((i) => matchesId(i, action.payload));
      if (item) {
        item.quantity += 1;
      }
    },

    /**
     * Decrement item quantity by composite identifier
     */
    decrementQuantity: (state, action: PayloadAction<string>) => {
      const item = state.items.find((i) => matchesId(i, action.payload));
      if (item) {
        if (item.quantity <= 1) {
          state.items = state.items.filter((i) => !matchesId(i, action.payload));
        } else {
          item.quantity -= 1;
        }
      }
    },

    /**
     * Save item for later by composite identifier
     */
    saveForLater: (state, action: PayloadAction<string>) => {
      const itemIndex = state.items.findIndex((i) => matchesId(i, action.payload));

      if (itemIndex !== -1) {
        const item = state.items[itemIndex];
        state.savedForLater.push({
          product: item.product,
          selectedSize: item.selectedSize,
          savedAt: new Date().toISOString(),
        });
        state.items.splice(itemIndex, 1);
      }
    },

    /**
     * Move saved item back to cart by composite identifier (preserving size)
     */
    moveToCart: (state, action: PayloadAction<string>) => {
      const savedItemIndex = state.savedForLater.findIndex(
        (item) => matchesSavedId(item, action.payload)
      );

      if (savedItemIndex !== -1) {
        const savedItem = state.savedForLater[savedItemIndex];
        const existingCartItem = state.items.find(
          (item) =>
            item.product.id === savedItem.product.id &&
            item.selectedSize === savedItem.selectedSize
        );

        if (existingCartItem) {
          existingCartItem.quantity += 1;
        } else {
          state.items.push({
            product: savedItem.product,
            quantity: 1,
            selectedSize: savedItem.selectedSize,
            addedAt: new Date().toISOString(),
          });
        }

        state.savedForLater.splice(savedItemIndex, 1);
      }
    },

    /**
     * Remove saved item by composite identifier
     */
    removeSavedItem: (state, action: PayloadAction<string>) => {
      state.savedForLater = state.savedForLater.filter(
        (item) => !matchesSavedId(item, action.payload)
      );
    },

    /**
     * Clear cart
     */
    clearCart: (state) => {
      state.items = [];
      state.appliedCoupon = null;
    },

    /**
     * Apply a coupon to the cart
     */
    applyCoupon: (state, action: PayloadAction<AppliedCoupon>) => {
      state.appliedCoupon = action.payload;
    },

    /**
     * Remove the applied coupon
     */
    removeCoupon: (state) => {
      state.appliedCoupon = null;
    },

    /**
     * Clear saved items
     */
    clearSavedItems: (state) => {
      state.savedForLater = [];
    },

    /**
     * Replace all saved-for-later items (used after Firebase load/merge)
     */
    setSavedForLaterItems: (state, action: PayloadAction<SavedItem[]>) => {
      state.savedForLater = action.payload;
    },

    setSavedForLaterLoading: (state, action: PayloadAction<boolean>) => {
      state.savedForLaterLoading = action.payload;
    },

    /**
     * Toggle cart drawer
     */
    toggleCartDrawer: (state) => {
      state.isDrawerOpen = !state.isDrawerOpen;
    },

    /**
     * Open cart drawer
     */
    openCartDrawer: (state) => {
      state.isDrawerOpen = true;
    },

    /**
     * Close cart drawer
     */
    closeCartDrawer: (state) => {
      state.isDrawerOpen = false;
    },
  },
});

// Export actions
export const {
  addToCart,
  removeFromCart,
  updateQuantity,
  incrementQuantity,
  decrementQuantity,
  saveForLater,
  moveToCart,
  removeSavedItem,
  clearCart,
  applyCoupon,
  removeCoupon,
  clearSavedItems,
  setSavedForLaterItems,
  setSavedForLaterLoading,
  toggleCartDrawer,
  openCartDrawer,
  closeCartDrawer,
} = cartSlice.actions;

// Selectors
export const selectCartItems = (state: RootState) => state.cart.items;
export const selectSavedItems = (state: RootState) => state.cart.savedForLater;
export const selectIsCartDrawerOpen = (state: RootState) => state.cart.isDrawerOpen;

export const selectCartItemCount = (state: RootState) =>
  state.cart.items.reduce((count, item) => count + item.quantity, 0);

export const selectCartSubtotal = (state: RootState) =>
  state.cart.items.reduce(
    (total, item) => total + item.product.price * item.quantity,
    0
  );

export const selectCartSummary = (state: RootState) => {
  const rules = state.settings?.general?.businessRules;
  const freeShippingThreshold = rules?.freeShippingThreshold ?? FREE_SHIPPING_THRESHOLD;
  const defaultShippingCost = rules?.defaultShippingCost ?? DEFAULT_SHIPPING_COST;
  const gstRate = rules?.gstRate ?? GST_RATE;

  const subtotal = state.cart.items.reduce(
    (total, item) => total + item.product.price * item.quantity,
    0
  );
  const isFreeShipping = subtotal >= freeShippingThreshold;
  const shipping = isFreeShipping ? 0 : defaultShippingCost;
  const tax = Math.round(subtotal * gstRate);

  // Coupon discount is recomputed against the live subtotal so it stays
  // consistent if the cart changes; the order transaction is the final authority.
  const appliedCoupon = state.cart.appliedCoupon;
  const discount = appliedCoupon
    ? computeCouponDiscount(appliedCoupon, subtotal)
    : 0;

  const total = Math.max(0, subtotal + shipping + tax - discount);
  const amountToFreeShipping = isFreeShipping
    ? 0
    : freeShippingThreshold - subtotal;

  return {
    itemCount: state.cart.items.reduce((count, item) => count + item.quantity, 0),
    subtotal,
    shipping,
    tax,
    discount,
    total,
    isFreeShipping,
    amountToFreeShipping,
  };
};

export const selectAppliedCoupon = (state: RootState) => state.cart.appliedCoupon;

export const selectIsInCart = (productId: string, selectedSize?: string) => (state: RootState) => {
  const identifier = selectedSize ? `${productId}::${selectedSize}` : productId;
  return state.cart.items.some((item) => matchesId(item, identifier));
};

export const selectSavedForLaterLoading = (state: RootState) =>
  state.cart.savedForLaterLoading;

export default cartSlice.reducer;
